/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';

import ContentFrame from '../../applicationFrame/ContentFrame';
import { ScrollAwareScroller, ScrollAwareContext, ScrollAwareAppNavStep } from '../../components/ScrollAware';
import ContentWrapper from '../../components/ContentWrapper';
import * as AttributePrimitives from './components/AttributePrimitives';

import { ObservationOccurrenceAppNav } from './components/OccurrenceAppNav';

import { useNavigate } from '../../hooks/useNavigate';
import RouteNames from '../../appAuthorized/RouteNames';
import ContentLoader from '../../components/ContentLoader';
import { ObservationFooter } from './components/OccurrenceFooter';

import { useActiveUser } from '../../hooks/useUser';
import { useShareCardListForActiveProfile } from '../../hooks/useShareCard';
import { useLists } from '../../hooks/useLists';
import { useExistingTrackableForActiveProfile } from '../../hooks/useTrackable';
import { Card, Icons } from '../../../bphComponents/bundle';
import { ObservationComments } from './components/OccurrenceComments';
import { ObservationFiles } from './components/OccurrenceFiles';
import COLORS from '../../styles/Colors';
import { ObservationAttributeModel, iObservation, iTrackable, iTrackableDefinition, ObservationModel, TrackableDefinitionModel, AttributeQuantity } from '../../../apiServices/apiModels/Trackable';
import PrimitiveTitleRow from './components/AttributePrimitives/PrimitiveTItleRow';
import { useUIState } from '../../hooks/useUIState';

interface AdditionalAttributesProps {
  trackable: iTrackable;
  languageCode: string;
  observation: iObservation;
  trackableTermLookup: any;
  trackableDefinition: iTrackableDefinition;
  onChange(d: iObservation): void;
  // onFocus(): void;
}

const Attribute = (props) => {
  const { attributeDefinition, trackable, languageCode, observationData, trackableTermLookup, onChange, onFocus, autoClose } = props;

  const observation = new ObservationModel(observationData, languageCode);
  const attValue = observation.getAttributeValue(attributeDefinition, trackable);

  switch (attributeDefinition.category) {
    case 'scale':

      return (
        <AttributePrimitives.Scale
          isDefaultExpanded={false}
          terms={trackableTermLookup}
          value={attValue}
          languageCode={languageCode}
          definition={attributeDefinition}
          onChange={(data) => {
            const obs = new ObservationModel(observationData, languageCode);
            obs.setAttribute(attributeDefinition.id, data);
            onChange(obs.data);
          }}
        />
      );
    case 'list':
      return (
        <AttributePrimitives.ListSelect
          terms={trackableTermLookup}
          listValue={attValue}
          languageCode={languageCode}
          listDefinition={attributeDefinition}
          onChange={(data) => {
            const obs = new ObservationModel(observationData, languageCode);
            obs.setAttribute(attributeDefinition.id, data);
            onChange(obs.data);
            const newAtt = ObservationAttributeModel.create(obs.data, attributeDefinition, trackableTermLookup, languageCode);

            if (newAtt.hasValue) {
              autoClose();
            }
          }}
        />
      );
    case 'quantity':
      return (
        <AttributePrimitives.Quantity
          quantityValue={attValue}
          definition={attributeDefinition}
          trackable={trackable}
          terms={trackableTermLookup}
          languageCode={languageCode}
          onFocus={onFocus}
          onChange={(data) => {
            const obs = new ObservationModel(observationData, languageCode);
            const oldAtt = ObservationAttributeModel.create(observationData, attributeDefinition, trackableTermLookup, languageCode);
            obs.setAttribute(attributeDefinition.id, data);
            onChange(obs.data);
            const newAtt = ObservationAttributeModel.create(obs.data, attributeDefinition, trackableTermLookup, languageCode);
            const unitChanged = newAtt.hasDifferentUnit(oldAtt.value);

            if (newAtt.hasValue && newAtt.hasUnit && unitChanged) {
              autoClose();
            }
          }}
          haveInitialFocus={false}
          textPhrase={{
            inputLabel: {
              id: 'heValue.unit.addUnit.label',
            },
          }}
        />
      );
    default:
      return null;
  }
};


const PrimaryAttributes: React.FC<AdditionalAttributesProps> = (props) => {
  const { observation, trackableTermLookup, trackable, languageCode, onChange } = props;
  const def = new TrackableDefinitionModel(props.trackableDefinition);
  const primaryAtts = def.primaryAttributes();

  if (primaryAtts.length === 0) { return null; }

  return (
    <>
    {
      primaryAtts.map(a => (
        <Card.CardShape key={a.id}>
          <Attribute
            languageCode={languageCode}
            isDefaultExpanded={true}
            observationData={observation}
            trackable={trackable}
            attributeDefinition={a}
            trackableTermLookup={trackableTermLookup}
            onChange={onChange}
            autoClose={() => {}}
          />
        </Card.CardShape>
      ))
    }
    </>
  );
};

function scrollToPos(element) {
  setTimeout(() => {
    const rect = element.getBoundingClientRect();
    const headerHeight = 84;
    const top = rect.top - headerHeight - 48;

    window.scrollBy({
      left: 0,
      top,
      behavior: 'smooth',
    });
  }, 60);
}

const SecondaryAttribute = (props) => {
  const { isExpanded, observation, trackable, languageCode, attributeDefinition, terms, onChange, onFocus, onToggleExpand } = props;

  const att = ObservationAttributeModel.create(observation, attributeDefinition, terms, languageCode);
  const posRef = React.useRef(null);

  return (
    <Card.CardSection hasBottomBorder={true}>
      <div ref={posRef} />

      <PrimitiveTitleRow
        label={att.name}
        valueSummary={att.summarizedValue}
        onClick={(newIsExpanded) => {
          onToggleExpand(newIsExpanded);

          if (newIsExpanded) {
            scrollToPos(posRef.current);
          }
        }}
        isExpanded={isExpanded}
      />

      <Card.CardSection disabledPadding={['top', 'bottom']} style={{ backgroundColor: COLORS.csAppBGgray30 }}>
      {
        isExpanded ? (
          <Attribute
            observationData={observation}
            attributeDefinition={attributeDefinition}
            trackable={trackable}
            trackableTermLookup={terms}
            languageCode={languageCode}
            onFocus={() => {
              onFocus();
            }}
            onChange={(d) => {
              onChange(d);
            }}
            autoClose={() => {
              setTimeout(() => {
                onToggleExpand(false);
                scrollToPos(posRef.current);
              }, 100);
            }}
          />
        ) : null
      }
      </Card.CardSection>
    </Card.CardSection>
  );
};

const SecondaryAttributes: React.FC<AdditionalAttributesProps> = (props) => {
  const { trackable, languageCode, observation, trackableTermLookup, onChange } = props;

  const def = new TrackableDefinitionModel(props.trackableDefinition);
  const secAtts = def.secondaryAttributes();
  const [expandedState, setExpandedState] = React.useState(() => {
    return {
      isExpandedList: secAtts.length === 1 ? [secAtts[0].id] : [],
      lastAttribute: '',
    };
  });

  if (secAtts.length === 0) { return null; }

  return (
    <Card.CardShape>
      {
        secAtts.map(att => {
          const attId = att.id;
          const isExpanded = expandedState.isExpandedList.includes(attId);
          return (
            <SecondaryAttribute
              key={attId}
              isExpanded={isExpanded}
              observation={observation}
              trackable={trackable}
              languageCode={languageCode}
              attributeDefinition={att}
              terms={trackableTermLookup}
              onChange={(d) => {
                if (expandedState.lastAttribute !== attId) {
                  setExpandedState({
                    ...expandedState,
                    lastAttribute: attId,
                  });
                }
                onChange(d);
              }}
              onFocus={() => {
                let newExpandedList = [...expandedState.isExpandedList];
                if (expandedState.lastAttribute) {
                  newExpandedList = newExpandedList.filter(a => a !== expandedState.lastAttribute);
                }
                if (!newExpandedList.includes(attId)) {
                  newExpandedList.push(attId);
                }
                setExpandedState({
                  isExpandedList: newExpandedList,
                  lastAttribute: attId,
                });
              }}
              onToggleExpand={() => {
                let newExpandedList = [...expandedState.isExpandedList];
                const lastId = expandedState.lastAttribute;
                const isExpanded = newExpandedList.includes(attId);

                if (isExpanded) {
                  newExpandedList = newExpandedList.filter(a => a !== attId);
                } else if (lastId) {
                  newExpandedList = newExpandedList.filter(a => a !== lastId);
                  newExpandedList.push(attId);
                } else {
                  newExpandedList.push(attId);
                }

                setExpandedState({
                  lastAttribute: isExpanded ? '' : attId,
                  isExpandedList: newExpandedList,
                });
              }}
            />
          );
        })
      }
    </Card.CardShape>
  );
};

interface ObservationViewProps {
  trackable: iTrackable;
  observation: iObservation;
  trackableTermLookup: any;
  trackableDefinition: iTrackableDefinition;
  trackableTermName: string;
  languageCode: string;
  isLoaded: boolean;
  onCancel(): void;
  onSubmit(o): void;
  onDelete(): void;
}

const ObservationView: React.FC<ObservationViewProps> = (props) => {
  const { languageCode, trackable, trackableTermName, trackableDefinition, trackableTermLookup, onSubmit, onDelete } = props;
  const [scData] = useShareCardListForActiveProfile();
  const [listData] = useLists();
  const [, uiActions] = useUIState();
  const isLoaded = props.isLoaded && scData.isLoaded && listData.isLoaded && Boolean(props.observation) && Boolean(props.trackableDefinition);

  const [observation, setObservation] = React.useState(props.observation);
  const o = new ObservationModel(observation, languageCode, trackableTermLookup);

  React.useEffect(() => {
    if (!isLoaded) { return; }

    setObservation(props.observation);
  }, [isLoaded]);

  if (!isLoaded || !observation) { return null; }

  return (
    <div>
      <ScrollAwareContext>
        <ScrollAwareAppNavStep
          submitDisabled={!o.isValid}
          onDisabledClick={() => {
            const msg = o.viewLevelValidationErrorMessages;
            if (msg.length > 0) {
              uiActions.showHintNotification(msg[0]);
            }
          }}
          cancel={null}
          done={() => {
            onSubmit(observation);
          }}
          color="white"
        />

        <ContentFrame className="bkgd-color-light-neutral">
          <ScrollAwareScroller>

            <ObservationOccurrenceAppNav
              trackableTermName={trackableTermName}
            />

            <ContentWrapper>
              <ContentLoader isContentLoaded={isLoaded} blockRenderUntilLoaded={true} isFixed={true}>

                <Card.ExternalCardTitle>
                  <FormattedMessage id="healthEntry.relatedTracking.label" />
                  <Icons.IconTracking.Small />
                </Card.ExternalCardTitle>

                <PrimaryAttributes
                  observation={observation}
                  languageCode={languageCode}
                  onChange={setObservation}
                  trackable={trackable}
                  trackableTermLookup={trackableTermLookup}
                  trackableDefinition={trackableDefinition}
                />

                <Card.CardShape>
                  <AttributePrimitives.DateTime
                    allowNull={true}
                    allowNow={true}
                    dateData={observation.date}
                    onChange={(d) => {
                      const o = new ObservationModel(observation, languageCode);
                      o.setDateTime(d);
                      setObservation(o.data);
                    }}
                  />
                </Card.CardShape>

                <SecondaryAttributes
                  observation={observation}
                  trackable={trackable}
                  languageCode={languageCode}
                  onChange={(d) => {
                    setObservation(d);
                  }}
                  trackableTermLookup={trackableTermLookup}
                  trackableDefinition={trackableDefinition}
                />

                <Card.CardShape>
                  <Card.CardContent>
                    <ObservationComments
                      comment={observation.comment}
                      onChange={(c) => {
                        const o = new ObservationModel(observation, languageCode);
                        o.setCommentValue(c);
                        setObservation(o.data);
                      }}
                    />
                  </Card.CardContent>
                </Card.CardShape>

                <ObservationFiles observation={observation} />

                <ObservationFooter
                  observation={observation}
                  onDelete={onDelete}
                  onToggleFlag={() => {
                    const o = new ObservationModel(observation, languageCode);
                    o.toggleIsFlagged();
                    setObservation(o.data);
                  }}
                />

              </ContentLoader>
            </ContentWrapper>
          </ScrollAwareScroller>
        </ContentFrame>
      </ScrollAwareContext>
    </div>
  );
};

interface Props {
  match: any;
}

export const ExistingObservationView: React.FC<Props> = (props) => {
  const observationId = Number(props.match.params.observationId);
  const trackableId = Number(props.match.params.trackableId);
  const [{ trackable, trackableDefinition, trackableTermLookup, observationLookup, isLoaded }, trackableActions] = useExistingTrackableForActiveProfile(trackableId);
  const [, navActions] = useNavigate();
  const [{ preferredLanguageCode }] = useActiveUser();
  const trackableTerm = trackableTermLookup[trackable.trackable_term_id] || {};

  return (
    <ObservationView
      trackable={trackable}
      languageCode={preferredLanguageCode}
      observation={observationLookup[observationId]}
      trackableTermName={trackableTerm.name}
      trackableDefinition={trackableDefinition}
      trackableTermLookup={trackableTermLookup}
      isLoaded={isLoaded}
      onSubmit={(newObservation) => {
        trackableActions.apiUpdateObservation(newObservation)
          .then(() => {
            navActions.goBack();
          });
      }}
      onCancel={() => {
        navActions.goBack();
      }}
      onDelete={() => {
        trackableActions.apiDeleteObservation(observationId)
          .then(() => {
            navActions.goBack(RouteNames.trackableObservationList(trackableId));
          });
      }}
    />
  );
};

export default ObservationView;
